import { inject, injectable } from 'inversify'
import { isString } from 'lodash-es'
import type { Resolvable } from '@/services/resolvers/types'
import { SERVICE_TYPES } from '@/core/container/types'
import type RouterService from '@/services/route/routerService'
import type EmailCampaignDomainService from '@/services/domain/campaign/emailCampaignDomainService'
import { TmApiNotFoundError } from '@/core/error/transport/tmApiNotFoundError'
import type EmailCampaignEmailSenderService from '@/services/domain/campaign/emailCampaignEmailSenderService'
import { TmApiAccessError } from '@/core/error/transport/tmApiAccessError'
import { EmailCampaignDomainStatus } from '@/data/models/domain/campaign/EmailCampaignDomain'
import type { TmRoutesDictionary } from '@/services/route/routerTypes'
import { UserSettingsRoutes } from '@/routes/user/settings'

@injectable()
export default class SettingsEmailCampaignDomainResolverService implements Resolvable {
  constructor(
    @inject(SERVICE_TYPES.RouterService) protected readonly routerService: RouterService,
    @inject(SERVICE_TYPES.EmailCampaignDomainService)
    protected readonly emailCampaignDomainService: EmailCampaignDomainService,
    @inject(SERVICE_TYPES.EmailCampaignEmailSenderService)
    protected readonly emailCampaignEmailSenderService: EmailCampaignEmailSenderService,
  ) {}

  public async resolve(): Promise<void> {
    const currentRoute = this.routerService.getCurrentRoute()
    const paramsKey: keyof TmRoutesDictionary[UserSettingsRoutes.EmailFlow]['params'] = 'domainId'
    const domainId = currentRoute.params[paramsKey]

    if (domainId && isString(domainId)) {
      try {
        const domain = await this.emailCampaignDomainService.loadDomain(domainId)
        if (domain?.status === EmailCampaignDomainStatus.Rejected) {
          await this.routerService.replace({ name: UserSettingsRoutes.EmailDomains })
          return
        }

        const emails = this.emailCampaignEmailSenderService.findSenderEmailsByDomain(domainId)
        if (domain && !emails.length) {
          await this.emailCampaignEmailSenderService.fetchEmailSendersByDomain(domain.id)
        }
      } catch (e) {
        if (e instanceof TmApiNotFoundError || e instanceof TmApiAccessError) {
          await this.routerService.replace({ name: UserSettingsRoutes.Email })
          return
        }
        throw e
      }
    }
  }
}
